import { ComponentType, createContext, EventHandler, MouseEvent, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { generateUEID, ToastPosition } from '@components/Toast/utils'

export interface ToastOptions {
  message: string
  buttonText?: string
  onClick?: EventHandler<MouseEvent>
  onDismiss?: () => void
  duration?: number
  position?: ToastPosition
  Component?: ComponentType<any>
  customProps?: Record<string, unknown>
}

export interface Toast {
  id: string
  options: ToastOptions
  dismiss: () => void
}

interface ToastStates {
  queue: Toast[],
  push: (options: ToastOptions) => Toast
  remove: (id: string) => void
}

export const ToastContext = createContext<ToastStates>({
  queue: [],
  push: () => undefined,
  remove: () => undefined
})

const useToast = () => useContext(ToastContext)

export default useToast

const dismissToast = (toastId: string, queue: Toast[]) => {
  const index = queue.findIndex(t => t.id === toastId)
  if (index !== -1) {
    const toast = queue[index]
    const { options: { onDismiss } } = toast
    onDismiss && onDismiss()

    queue.splice(index, 1)
  }

  return [...queue]
}

export const useToastStates = (): ToastStates => {
  const [queue, setQueue] = useState([])
  const ref = useRef(queue)

  const push = useCallback((options: ToastOptions) => {
    const nextId = generateUEID()
    const toast: Toast = {
      id: nextId,
      options,
      dismiss: () => {
        ref.current = dismissToast(nextId, ref.current)
        setQueue(ref.current)
      }
    }
    const q = ref.current
    ref.current = [...q, toast]
    setQueue(ref.current)
    return toast
  }, [setQueue])

  const remove = useCallback((id: string) => {
    ref.current = dismissToast(id, ref.current)
    setQueue(ref.current)
  }, [setQueue])

  return useMemo(() => ({
    queue,
    push,
    remove
  }), [queue, push, remove])
}
